import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookMarked, Loader2, RefreshCw, Save, Wand2, ChevronDown, ChevronUp } from "lucide-react";

// ATLAS-BOOK-BIBLE: the translation brief fixed before translation, per edition.
const FORM = { prose: "proză", verse: "vers", drama: "dramaturgie", mixed: "mixt" };
const ADDR = { formal: "formal", informal: "informal", neutral_none: "fără distincție" };
const CAPS = { every_line: "majusculă la fiecare vers", sentence_only: "majusculă doar la început de propoziție", free: "liber" };

export function BiblePanel({ edition, onChanged }) {
  const [open, setOpen] = useState(false);
  const [bible, setBible] = useState(null);
  const [terms, setTerms] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setBusy("load");
    try {
      const { data } = await api.get(`/editions/${edition.id}/bible`);
      setBible(data.bible);
      setTerms((data.bible?.terms || []).map((t) => ({ ...t })));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut citi biblia");
    } finally { setBusy(""); }
  };
  const toggle = async () => { if (!open && !bible) await load(); setOpen(!open); };

  const rebuild = async () => {
    setBusy("build");
    try {
      const { data } = await api.post(`/editions/${edition.id}/bible/build`);
      setBible(data.bible); setTerms(data.bible.terms.map((t) => ({ ...t })));
      toast.success("Biblia a fost reconstruită");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reconstruirea a eșuat");
    } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/editions/${edition.id}/bible`, { terms: terms.filter((t) => t.source && t.target) });
      setBible(data.bible); setTerms(data.bible.terms.map((t) => ({ ...t })));
      toast.success("Biblia a fost salvată");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Salvarea a eșuat");
    } finally { setBusy(""); }
  };
  const enforce = async () => {
    setBusy("enforce");
    try {
      const { data } = await api.post(`/editions/${edition.id}/bible/enforce`);
      toast.success(`Aplicată pe text: ${data.changed} pasaje modificate, ${data.verse_mismatches} strofe cu număr diferit de versuri`);
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aplicarea a eșuat");
    } finally { setBusy(""); }
  };

  if (edition.is_source_edition) return null;
  return (
    <div className="mt-3 rounded-xl border border-[#e6e4dd] bg-white p-3 text-xs" data-testid={`bible-${edition.lang}`}>
      <button onClick={toggle} className="flex w-full items-center gap-1.5 font-medium text-[#0f172a]">
        <BookMarked className="h-3.5 w-3.5" /> Biblia cărții
        {busy === "load" ? <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin" /> : (open ? <ChevronUp className="ml-auto h-3.5 w-3.5" /> : <ChevronDown className="ml-auto h-3.5 w-3.5" />)}
      </button>
      {open && !bible && (
        <div className="mt-2 text-[#475569]">
          <p>Ediția nu are încă biblie. Se construiește automat la următorul Run, sau acum:</p>
          <Button size="sm" disabled={!!busy} onClick={rebuild} className="mt-2 h-7 rounded-lg bg-slate-900 px-2 text-xs text-white">
            {busy === "build" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}Construiește biblia
          </Button>
        </div>
      )}
      {open && bible && (
        <div className="mt-2 space-y-2 text-[#334155]">
          <p><b>Operă:</b> {bible.period_and_genre}</p>
          <p><b>Formă:</b> {FORM[bible.form] || bible.form} · <b>Versuri:</b> {CAPS[bible.verse_line_caps] || bible.verse_line_caps} · <b>Ghilimele:</b> {bible.quotes?.open}…{bible.quotes?.close}</p>
          <p><b>Registru:</b> {bible.register_note}</p>
          <p><b>Adresare:</b> {ADDR[bible.address_form] || bible.address_form}{bible.address_note ? `: ${bible.address_note}` : ""}</p>
          {bible.caps_names === "normalize" && <p><b>Nume în MAJUSCULE în sursă:</b> scrise normal în traducere</p>}
          <div className="max-h-64 overflow-y-auto rounded-lg border border-[#e6e4dd]">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#faf9f6] text-left"><tr><th className="p-1.5">Sursă</th><th className="p-1.5">Traducere fixă</th></tr></thead>
              <tbody>
                {terms.map((t, i) => (
                  <tr key={t.source} className="border-t border-[#f1f0ec]">
                    <td className="p-1.5 font-mono2">{t.source}</td>
                    <td className="p-1"><Input value={t.target} className="h-7 text-xs"
                      onChange={(ev) => { const n = [...terms]; n[i] = { ...t, target: ev.target.value }; setTerms(n); }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[#94a3b8]">{bible.origin === "edited" ? "Editată manual" : `Generată automat (${bible.model})`}</p>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" disabled={!!busy} onClick={save} className="h-7 rounded-lg bg-slate-900 px-2 text-xs text-white">
              {busy === "save" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}Salvează
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={enforce} className="h-7 rounded-lg px-2 text-xs"
              title="Aplică regulile deterministe ale bibliei pe textul deja tradus (majuscule, versuri) și marchează strofele cu versuri unite sau despărțite">
              {busy === "enforce" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Wand2 className="mr-1 h-3 w-3" />}Aplică pe text
            </Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={rebuild} className="h-7 rounded-lg px-2 text-xs"
              title="Reconstruiește automat biblia; editarea manuală se pierde">
              {busy === "build" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}Reconstruiește
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
